import React, { useState } from 'react'

import { Box } from '@mui/material'
import CustomerFeaturePanel from 'features/customers/components/features/CustomerFeaturePanel'
import CustomerFeatureRow from 'features/customers/components/features/CustomerFeatureRow'
import CustomerQuotaRows, { QUOTA_LINES } from 'features/customers/components/features/CustomerQuotaRows'
import { QUOTAS_PANEL } from 'features/customers/constants/featureGroups'
import type { FeatureRowGroup } from 'features/customers/utils/featureRows'

type Props = {
  groups: FeatureRowGroup[]
  // Not a feature group: the quota lines have no plan value, no override and no history, so no
  // narrowing filter can honestly match them.
  showQuotas: boolean
  edits: Record<string, unknown>
  onChange: (name: string, value: unknown) => void
}

const CustomerFeaturesTable = ({ groups, showQuotas, edits, onChange }: Props) => {
  // Only what someone has actually toggled, so startsOpen still decides the rest. Seeding it at
  // mount meant a group the filter was hiding got no entry, and the map outlived the customer it was
  // built for - the table does not remount when only the id in the route changes.
  const [toggled, setToggled] = useState<Record<string, boolean>>({})

  const isOpen = (panel: { id: string; startsOpen: boolean }) => toggled[panel.id] ?? panel.startsOpen

  const toggle = (panel: { id: string; startsOpen: boolean }) =>
    setToggled(current => ({ ...current, [panel.id]: !isOpen(panel) }))

  // The floor is what a six-column row needs before a second column is worth having: on a MacBook
  // that means one full-width column, and two only on an external display. Below it the number field
  // clips and the usage text runs under the actor chip.
  //
  // API quotas sit after the two connector groups, which stay adjacent to each other.
  const afterConnectors = groups.findIndex(group => group.id === 'connectorBehaviour')
  const panels = [...groups] as (FeatureRowGroup | typeof QUOTAS_PANEL)[]

  if (showQuotas) panels.splice(afterConnectors === -1 ? panels.length : afterConnectors + 1, 0, QUOTAS_PANEL)

  return (
    // A container query, not a media query: the drawer changes how much room there actually is. The
    // threshold is two ~840px panels, which is the width at which a row stops being cramped - so a
    // MacBook gets one comfortable column and an external display gets two. Two is the ceiling.
    <Box sx={{ containerType: 'inline-size' }}>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          p: 2,
          gridTemplateColumns: '1fr',
          alignItems: 'start',
          '@container (min-width: 1700px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
        }}>
        {panels.map(panel => {
          const isQuotas = panel.id === QUOTAS_PANEL.id
          const group = panel as FeatureRowGroup

          return (
            <CustomerFeaturePanel
              color={panel.color}
              count={isQuotas ? QUOTA_LINES.length : group.rows.length}
              icon={panel.icon}
              key={panel.id}
              onToggle={() => toggle(panel)}
              open={isOpen(panel)}
              differing={isQuotas ? undefined : group.differing}
              overridden={isQuotas ? undefined : group.overridden}
              title={panel.title}>
              {isQuotas ? (
                <CustomerQuotaRows />
              ) : (
                group.rows.map(row => (
                  <CustomerFeatureRow edited={edits[row.name]} key={row.name} onChange={onChange} row={row} />
                ))
              )}
            </CustomerFeaturePanel>
          )
        })}
      </Box>
    </Box>
  )
}

export default CustomerFeaturesTable
